import type {
    Person,
    ProcessStep,
    ProductImage,
    ProductType,
    TimelineEntry
} from '@/types'

export const PROCESS_STEPS: Array<ProcessStep> = [
    {
        id: 'harvest',
        number: '1',
        title: 'Bark harvest',
        timing: 'Spring and autumn',
        text: "We cut young branches from the white mulberry trees that line our stream, the same trees that once fed Samarkand's silkworms. The bark is stripped while it is still green, the dark outer skin is scraped away, and only the pale inner layer is kept. Ten kilos of branches give about one kilo of fibre.",
        image: '/images/qogoz/step-harvest.jpg',
        alt: 'Weathered hands stripping long ribbons of bark from young mulberry branches'
    },
    {
        id: 'boiling',
        number: '2',
        title: 'Boiling',
        timing: 'Six hours over a wood fire',
        text: 'The inner bark goes into a copper cauldron with water and a little wood ash, and simmers until the stiff ribbons turn soft enough to pull apart with your fingers. Then it is rinsed in the stream, again and again, until the water runs clear.',
        image: '/images/qogoz/step-boiling.jpg',
        alt: 'A blackened copper cauldron of mulberry bark steaming over a wood fire in a courtyard'
    },
    {
        id: 'beating',
        number: '3',
        title: 'Beating with the water wheel',
        timing: 'A full day, without hands',
        text: 'Our wooden water wheel lifts a heavy hammer and lets it fall, about forty times a minute, onto the fibre in a stone trough. The stream does the work for a whole day. By evening the bark has become a creamy pulp, its long fibres separated but never cut, which is why the paper is so strong.',
        image: '/images/qogoz/step-beating.jpg',
        alt: 'A wooden hammer driven by a water wheel striking pale fibre pulp in a stone trough'
    },
    {
        id: 'forming',
        number: '4',
        title: 'Sheet forming',
        timing: 'One sheet at a time',
        text: 'The pulp is stirred into a vat of clear water. The papermaker dips a fine reed mould, lifts it level and gives it a short shake, so the fibres lock together into a single even sheet. Each sheet is laid on a board and dried against a sunlit wall.',
        image: '/images/qogoz/step-forming.jpg',
        alt: 'Hands lifting a reed mould from a vat of water with a fresh sheet of pulp on it'
    },
    {
        id: 'polishing',
        number: '5',
        title: 'Polishing with a stone',
        timing: 'Ten minutes for every sheet',
        text: "Finally each dry sheet is laid on a smooth board and rubbed with a polished agate stone, stroke after stroke, until the surface closes and takes on a soft sheen. This is where the name silk paper comes from. There is no silk in it, only patience, and ink glides across it without ever bleeding.",
        image: '/images/qogoz/step-polishing.jpg',
        alt: 'A hand burnishing a cream sheet of paper with a polished agate stone'
    }
]

export const PRODUCT_TYPES: Array<{ value: ProductType; label: string; singular: string }> = [
    { value: 'sheets', label: 'Sheets', singular: 'Sheets' },
    { value: 'notebooks', label: 'Notebooks', singular: 'Notebook' },
    { value: 'wedding', label: 'Wedding sets', singular: 'Wedding set' }
]

export const productTypeLabel = (type: ProductType): string =>
    PRODUCT_TYPES.find(entry => entry.value === type)?.singular ?? 'Paper'

// Shown after the product photo on its page until the product has its own gallery in the CMS
export const TYPE_DETAIL_IMAGES: Record<ProductType, Array<ProductImage>> = {
    sheets: [
        { src: '/images/qogoz/detail-deckle.jpg', alt: 'The deckled edge of a handmade sheet with loose mulberry fibres, backlit' },
        { src: '/images/qogoz/step-polishing.jpg', alt: 'A sheet being polished with an agate stone' }
    ],
    notebooks: [
        { src: '/images/qogoz/detail-pages.jpg', alt: 'The uneven deckled page edges inside a hand-sewn notebook' },
        { src: '/images/qogoz/detail-deckle.jpg', alt: 'The deckled edge of a handmade sheet with loose mulberry fibres, backlit' }
    ],
    wedding: [
        { src: '/images/qogoz/detail-deckle.jpg', alt: 'The deckled edge of a handmade sheet with loose mulberry fibres, backlit' },
        { src: '/images/qogoz/hero-paper.jpg', alt: 'Close up of polished mulberry paper with a soft torn edge' }
    ]
}

export const WORKSHOP = {
    pricePerPerson: 45,
    maxGuests: 8,
    duration: '4 hours',
    start: '9:30',
    days: 'Tuesday to Sunday',
    minimumAge: 8,
    closedWeekday: 1
}

export const WORKSHOP_AGENDA: Array<{ time: string; title: string; text: string }> = [
    {
        time: '9:30',
        title: 'Green tea under the mulberries',
        text: 'We meet in the courtyard, pour the first pot of tea and walk along the trees that give us our bark.'
    },
    {
        time: '10:00',
        title: 'Strip and scrape the bark',
        text: 'You cut a branch, peel it and scrape away the outer skin with a blunt knife, exactly as it was done in the tenth century.'
    },
    {
        time: '10:45',
        title: 'Inside the mill',
        text: 'Stand next to the water wheel while the hammer beats yesterday’s fibre into pulp, and feel the pulp between your fingers.'
    },
    {
        time: '11:30',
        title: 'Form your own sheets',
        text: 'At the vat, our papermakers guide your hands as you lift the reed mould. Most guests form three or four good sheets.'
    },
    {
        time: '12:45',
        title: 'Polish with the agate stone',
        text: 'You burnish a dry sheet from our wall until it shines, then try it with a reed pen and ink.'
    },
    {
        time: '13:15',
        title: 'Bread, apricots and a last look',
        text: 'A simple lunch in the courtyard before we say goodbye. Your own sheets dry overnight and we deliver them to your hotel.'
    }
]

export const WORKSHOP_INCLUDES: Array<string> = [
    'All materials and tools',
    'Green tea and a courtyard lunch',
    'The sheets you form, delivered to your Samarkand hotel the next day',
    'A polished sheet from our drying wall to take home',
    'Guidance in English or Russian'
]

export const WORKSHOP_FAQ: Array<{ question: string; answer: string }> = [
    {
        question: 'How do we get there?',
        answer: 'The atelier is about 25 minutes by taxi from the centre of Samarkand. Once your booking is confirmed we send directions you can show any driver, and we can arrange a car for you.'
    },
    {
        question: 'What should we wear?',
        answer: 'Clothes you do not mind getting wet at the cuffs, and closed shoes. Forming sheets is a gentle but splashy business. We lend aprons.'
    },
    {
        question: 'Can children join?',
        answer: 'Yes, from 8 years old, with an adult. Children usually love the water wheel most of all.'
    },
    {
        question: 'What if we need to cancel?',
        answer: 'Write to us up to 48 hours before your workshop and we will move your date or simply cancel. You only pay on the day, in cash or by card.'
    }
]

export const TIMELINE: Array<TimelineEntry> = [
    {
        year: '751',
        title: 'The secret reaches Samarkand',
        text: 'According to the old chronicles, Chinese papermakers taken prisoner after the Battle of Talas brought their craft to Samarkand. Local makers soon swapped hemp and rags for the bark of the mulberry trees that grew everywhere in the valley.'
    },
    {
        year: '10th c.',
        title: 'The paper of the Islamic world',
        text: 'Samarkand paper travelled with the caravans to Baghdad, Damascus and Cairo. Writers of the time noted that it had driven out papyrus and parchment. Polished with stones and shells, it carried Qurans, poetry, astronomy tables and letters of state.'
    },
    {
        year: '15th c.',
        title: 'Under Ulugh Beg',
        text: 'When the astronomer-king turned Samarkand into a city of observatories and madrasas, its paper was prized by scribes and miniature painters across the Timurid world, and the water mills along the canals kept turning.'
    },
    {
        year: '19th c.',
        title: 'The mills fall silent',
        text: 'Cheap factory paper arrived from Russia and Europe. One by one the water wheels stopped turning, and within a few generations the method survived only in a handful of old manuscripts and the memories of elderly craftsmen.'
    },
    {
        year: '1990s',
        title: 'The revival',
        text: 'Uzbek craftsmen and scholars began rebuilding the old mills, reading the manuscripts and testing bark, ash and stone until the sheets came out right again. Samarkand paper was made by hand once more.'
    },
    {
        year: '2014',
        title: "Qog'oz Atelier opens",
        text: 'Our master papermaker, trained at one of those revived mills, set up his own wheel on a quiet stream outside the city with his family. Today six of us make paper here, and the door is open to anyone who wants to try.'
    }
]

export const PEOPLE: Array<Person> = [
    {
        name: 'Bakhtiyor Rakhimov',
        role: 'Master papermaker and founder',
        text: 'Bakhtiyor learned the craft in the years of the revival and built our water wheel by hand. He still forms the large folio sheets himself, and decides which batches are good enough to leave the atelier.'
    },
    {
        name: 'Dilnoza Rakhimova',
        role: 'Dyer and sheet former',
        text: "Dilnoza looks after the indigo vat and the pomegranate dye pots in the courtyard. The colours on our wedding stationery are hers, mixed by eye and never the same twice."
    },
    {
        name: 'Farrukh Rakhimov',
        role: 'Bookbinder',
        text: 'The youngest of the family, Farrukh sews every notebook and journal at a bench by the drying wall, using linen and indigo thread.'
    },
    {
        name: 'Malika Usmonova',
        role: 'Workshop host',
        text: 'Malika welcomes our visitors, translates for Bakhtiyor and has taught more than three thousand people to lift their first sheet from the vat.'
    }
]
